import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { shuffle } from "../_helpers";
import { FeaturedListingCardData } from "../../shared/components/featured-listings/featured-listing-card.component";
import { I18nService } from "../../shared/services";
import { SpotBuildingSearchRequest } from "../models/spot-listing.model";
import { PagerService } from "./pager.service";

@Injectable()
export class ListingsService extends PagerService {
  private spotBuildingIdsAPI: string = `/spotbuildings/search/ids/fast`;
  private spotBuildingDetailsAPI: string = `/spotbuildings/search/detail`;
  private advertisements: string =
    "api/service-provider/carousel-data?application=SIILA";

  private loadedListings: FeaturedListingCardData[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  pageSize: number = 5;
  latamCountry = "";

  constructor(
    private http: HttpClient,
    private _log: NGXLogger,
    private i18nService: I18nService
  ) {
    super();
  }

  public get getCurrentPage(): number {
    return this.currentPage;
  }

  public get getTotalPageCount(): number {
    return this.totalCount;
  }

  async getListings() {
    let payload = { isFeaturedWithoutCheckingDate: true, randomOrder: true };
    const { body: ids }: { body: number[] } = (await this.searchSpotBuildingIds(
      payload,
      1
    )) as { headers: HttpHeaders; body: number[] };
    const spotBuildingList: FeaturedListingCardData[] = await this.getSpotBuildingDetails(
      1,
      ids.length,
      ids
    );
    // Shuffle on every load, whatever order the API returns, so each SPOT card
    // gets a fair share of the first slots.
    return shuffle(spotBuildingList);
  }

  async searchSpotBuildingIds(
    searchRequest: SpotBuildingSearchRequest,
    page: number
  ) {
    let urlSearchParams = new URLSearchParams();
    urlSearchParams.append("page", page.toString());
    urlSearchParams.append("per_page", "4");
    urlSearchParams.append("lang", this.i18nService.getCurrentLanguage());
    const host =
      this.latamCountry == "latamMx"
        ? environment.apiURLMX
        : this.latamCountry == "latamBr"
        ? environment.apiURLBR
        : environment.apiURL;

    return await this.http
      .post(
        host + this.spotBuildingIdsAPI + "?" + urlSearchParams,
        searchRequest,
        {
          observe: "response"
        }
      )
      .pipe(
        map(val => {
          return {
            headers: val.headers,
            body: val.body
          };
        })
      )
      .toPromise();
  }

  async getSpotBuildingDetails(
    page: number,
    pageSize: number,
    spotBuildingIds: number[]
  ): Promise<FeaturedListingCardData[]> {
    // `lang` lets the backend return each listing's SPOT `detailsURL` in the
    // page language, so cards can render a real href without one call per card.
    let params = `&lang=${this.getSpotLanguage()}`;

    let detailsURL =
      (this.latamCountry == "latamMx"
        ? environment.apiURLMX
        : this.latamCountry == "latamBr"
        ? environment.apiURLBR
        : "/api") +
      `${this.spotBuildingDetailsAPI}?page=${page}&per_page=${pageSize}${params}`;

    return await this.http
      .post<FeaturedListingCardData[]>(detailsURL, spotBuildingIds)
      .toPromise();
  }

  /** Language code the SPOT URL endpoints expect (`pt-br` instead of `pt`). */
  private getSpotLanguage(): string {
    const lang = this.i18nService.getCurrentLanguage();
    return lang === "pt" ? "pt-br" : lang;
  }

  getAdvertisements(size?: number) {
    if (size) {
      return this.http
        .get(this.advertisements + (size ? "&size=" + size : ""), {
          observe: "response"
        })
        .pipe(map(val => val.body));
    } else {
      return this.http
        .get(this.advertisements, {
          observe: "response"
        })
        .pipe(map(val => val.body));
    }
  }

  async getActiveListingDetails() {
    return null;
  }

  public setTotalCount(totalCountHeader: string) {
    if (!totalCountHeader) {
      return;
    }
    this.totalCount = parseInt(totalCountHeader);
  }

  public appendListings(listings: FeaturedListingCardData[]) {
    this.loadedListings = [...this.loadedListings, ...listings];
  }

  fetchPage(page: number) {
    console.error("fetchPage not implemented in ListingsService");
  }
}
